import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Item, TimelineType } from '@/types/item';
import { Modal } from '@/types/modal';
import Grid, { GridAction, itemCustom, itemText } from '@/components/molecules/section/Grid';
import { Label, ValueButton, ValueText } from '@/components/atoms/Section';
import Container from '@/components/molecules/section/Container';
import ModalView from '@/components/modals/Modal';
import { useOrderValue, useTab } from '@/hooks/tab';
import { Tab } from '@/types/hashes';
import { Tooltip, Snackbar, IconButton, Chip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FlagId } from '@/types/common';
import { BootService } from '@/services/boot';

const View: React.FC<{ item: Item; sellerId?: number }> = ({ item, sellerId }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [, setInputValue] = useOrderValue();
  const [, setTab] = useTab();

  const isAiListing = item.flags && item.flags?.findIndex((flag) => flag.id === FlagId.AI_LISTING) >= 0;

  const isProduction = BootService.env === 'prod' || BootService.env === 'production';

  const curationPriorityAction: GridAction = {
    kind: 'Button',
    label: t('item.basic_info.actions.prioritise_curation'),
    onClick: () => setModal({ kind: 'ItemCurationPriority', item, onClose: () => setModal(null) }),
  };

  const withdrawAction: GridAction = {
    kind: 'Button',
    label: t('item.basic_info.actions.withdraw_item'),
    disabled: !item.enableWithdrawFromSale,
    onClick: () => setModal({ kind: 'ItemWithdrawFromSale', item, onClose: () => setModal(null) }),
  };

  const payByLinkAction: GridAction = {
    kind: 'Button',
    label: t('item.basic_info.actions.pay_by_link'),
    disabled: ['authorized', 'captured', 'created'].includes(item?.payByLinkStatus?.toLowerCase() || ''),
    onClick: () => setModal({ kind: 'PayByLink', item, onClose: () => setModal(null) }),
  };

  const handleItemClick = (id: string) => {
    setInputValue(id);
    setTab(Tab.ORDER);
  };

  const actions: GridAction[] =
    item.isSold && item.isSold === '1'
      ? [
          {
            kind: 'Button',
            label: t('item.basic_info.actions.timeline_seller'),
            disabled: !item.sellerId,
            onClick: () =>
              setModal({ kind: 'Timeline', type: TimelineType.SELLER, item, sellerId, onClose: () => setModal(null) }),
          },
          {
            kind: 'Button',
            label: t('item.basic_info.actions.timeline_buyer'),
            disabled: !item.buyerId || !item.orderId,
            onClick: () =>
              setModal({ kind: 'Timeline', type: TimelineType.BUYER, item, onClose: () => setModal(null) }),
          },
          withdrawAction,
          payByLinkAction,
          ...(!isProduction ? [curationPriorityAction] : []),
        ]
      : [withdrawAction, payByLinkAction, ...(!isProduction ? [curationPriorityAction] : [])];

  const getTemplateText = () => {
    try {
      const parts = [item?.brandName, item?.productName].filter(Boolean);

      let text = parts.join(' ');

      if (item?.productId) {
        text += text ? ` (ref. ${item.productId})` : `(ref. ${item.productId})`;
      }

      if (!text && item?.productName) {
        text = item.productName;
      }

      return text;
    } catch (error) {
      return '';
    }
  };

  const copyProductIdByTemplate = () => {
    const text = getTemplateText();

    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const items = [
    itemCustom(
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Label labelInfo={{ label: t('item.basic_info.id_product') }} />
          {isAiListing && (
            <Chip
              label={t('item.basic_info.ai_listing')}
              color="success"
              size="small"
              sx={{ marginLeft: 1, color: 'white', my: -2 }}
            />
          )}
        </div>
        <ValueText
          kind="Text"
          label={item?.productId.toString() || ''}
        />
      </>,
    ),
    itemCustom(
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Label labelInfo={{ label: t('item.basic_info.product_type') }} />
          <Tooltip
            title={`${t('item.basic_info.copy_tooltip')}: ${getTemplateText()}`}
            arrow
          >
            <IconButton
              onClick={copyProductIdByTemplate}
              size="small"
              style={{ padding: '2px', marginLeft: '4px' }}
            >
              <ContentCopyIcon style={{ fontSize: '14px' }} />
            </IconButton>
          </Tooltip>
        </div>
        <ValueText
          kind="Text"
          label={item?.productName || ''}
        />
      </>,
    ),
    itemText(t('item.basic_info.listed_price'), item.initialPrice),
    itemText(t('item.basic_info.price_sold'), item.price),
    itemText(t('item.basic_info.seller_receive_amount'), item.netSellingPrice),
    itemText(t('item.basic_info.commission'), item.commission),
    itemText(t('item.basic_info.percentage_margin'), item.percentageMargin),
    itemText(t('item.basic_info.last_action'), item.productLastAction),
    itemText(t('item.basic_info.reserved'), item.isReserved ? t('common.yes') : t('common.no')),
    itemCustom(
      <>
        <Label labelInfo={{ label: t('item.basic_info.payment_failed_orders') }} />
        {item.paymentFailedOrders?.length ? (
          item.paymentFailedOrders.map((item) => (
            <ValueButton
              key={item}
              label={item.toString()}
              onClick={() => handleItemClick(item.toString())}
            />
          ))
        ) : (
          <ValueText
            label={'-'}
            kind={'Text'}
          />
        )}
      </>,
    ),
    itemText(t('item.basic_info.seller_cancellation_reason'), item.productUnavailabilityReasonStatus),
    itemText(t('item.basic_info.pay_by_link'), item.payByLinkStatus, {
      kind: 'Button',
      label: t('item.basic_info.update_description'),
      disabled: !item.payByLinkStatus,
      onClick: () => setModal({ kind: 'PayByLinkDetail', item, onClose: () => setModal(null) }),
    }),
  ];

  return (
    <>
      <Container
        title={t('item.basic_info.title')}
        actions={actions}
        testId="basic_info"
      >
        <Grid
          column={5}
          items={items}
        />
      </Container>
      <ModalView modal={modal} />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={t('item.basic_info.copied_message')}
      />
    </>
  );
};

export default View;
